import { EventsSearchObject } from '@/common/types';
import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { entityEventsApi, EventsContextType } from '@/rtk/entityEventsApi';

type UtcDates = {
  end: string;
  start: string;
};

export type FiltersState = {
  search: EventsSearchObject;
  levels: string[];
  utcDates: UtcDates;
  offset: number;
  selectedEventId: string | null;
  searchError: string | null;
};

export const initialState: FiltersState = {
  search: {},
  levels: [],
  utcDates: {
    start: '',
    end: '',
  },
  selectedEventId: null,
  offset: 0,
  searchError: null,
};

export const eventFilters = createSlice({
  name: 'eventFilters',
  initialState,
  reducers: {
    setSearch(state, action: PayloadAction<EventsSearchObject>) {
      state.search = action.payload;
    },

    setLevels(state, action: PayloadAction<string[]>) {
      state.levels = action.payload;
    },

    setUtcDates(state, action: PayloadAction<UtcDates>) {
      state.utcDates = action.payload;
    },

    setOffset(state, action: PayloadAction<number>) {
      state.offset = action.payload;
    },

    setSelectedEventId(state, action: PayloadAction<string | null>) {
      state.selectedEventId = action.payload;
    },

    resetSelectedEvent(state) {
      state.selectedEventId = null;
    },

    setSearchError(state, action: PayloadAction<string | null>) {
      state.searchError = action.payload;
    },

    setAllFilters(state, action: PayloadAction<Partial<FiltersState>>) {
      for (const key of Object.keys(action.payload) as (keyof Omit<
        FiltersState,
        'selectedEventId'
      >)[]) {
        const value = action.payload[key];

        if (value !== undefined) {
          state[key] = value as any;
        }
      }
    },

    resetFilters: () => initialState,
  },
  extraReducers: (builder) => {
    // Listens for getMoreEvents response and syncs the dates to the slice
    builder.addMatcher(
      isAnyOf(entityEventsApi.endpoints.getMoreEvents.matchFulfilled),
      (state: FiltersState, action) => {
        const payload = action.payload;
        const args = action.meta.arg.originalArgs;

        if (!payload.length) return;

        const firstEvent = payload[0];
        const lastEvent = payload[payload.length - 1];

        switch (args.type) {
          case EventsContextType.Succeeding:
            state.utcDates = {
              ...state.utcDates,
              start: state.utcDates?.start || '',
              end: firstEvent.eventTimestamp || '',
            };

            break;

          case EventsContextType.Preceding:
            state.utcDates = {
              start: lastEvent.eventTimestamp,
              end: state.utcDates?.end || '',
            };

            break;

          case EventsContextType.Context:
            state.utcDates = {
              start: lastEvent.eventTimestamp,
              end: firstEvent.eventTimestamp,
            };

            break;

          default:
            break;
        }
      },
    );
  },
});

export const {
  setSearch,
  setLevels,
  setOffset,
  setAllFilters,
  setUtcDates,
  setSelectedEventId,
  resetSelectedEvent,
  setSearchError,
} = eventFilters.actions;

export default eventFilters.reducer;
